import {Phone, Calendar, Shield, Sparkles, Car, Award, Check, Zap, MapPin} from 'lucide-react'
import { useNavigate } from "react-router-dom"
import { useEffect } from "react"
import CanonicalTag from '@/components/CanonicalTag'

const PpfPowayPage = () => {
  const navigate = useNavigate()

  useEffect(() => {
    const script = document.createElement('script')
    script.type = 'application/ld+json'
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "How much does PPF cost in Poway?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Partial front PPF starts at $1,299, full front starts at $1,899, and full vehicle coverage starts at $4,499. Pricing varies by vehicle make and model. We provide detailed quotes for Poway residents."
          }
        },
        {
          "@type": "Question",
          "name": "Is PPF worth it for Poway's driving conditions?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes. Poway's highway access via I-15 and Pomerado Road means constant rock chip exposure. PPF prevents cumulative damage from daily commuting to Rancho Bernardo, Scripps Ranch, and downtown San Diego."
          }
        },
        {
          "@type": "Question",
          "name": "How long does PPF last in Poway?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Premium PPF lasts 10+ years in Poway's inland climate. Self-healing technology and UV resistance ensure long-term protection against sun damage and environmental wear."
          }
        },
        {
          "@type": "Question",
          "name": "Can you install PPF on trucks and SUVs in Poway?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Absolutely. We install PPF on trucks, SUVs, and off-road vehicles popular in Poway. Coverage includes front bumpers, hoods, fenders, rocker panels, and bed rails for comprehensive protection."
          }
        }
      ]
    })
    document.head.appendChild(script)

    document.title = "Paint Protection Film Poway | PPF Installation for Trucks, SUVs & Tesla"
    const metaDescription = document.querySelector('meta[name="description"]')
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Expert paint protection film installation in Poway, CA. Protect trucks, SUVs, Tesla with professional PPF. Self-healing technology, manufacturer-backed film warranties. Serving Poway, Rancho Bernardo, Scripps Ranch, and inland San Diego.')
    }

    return () => {
      document.head.removeChild(script)
    }
  }, [])

  return (
    <>
      <CanonicalTag url="https://autowrapsandiego.com/ppf-poway" />
      <div className="pt-20 min-h-screen bg-black">
      {/* Hero */}
      <section className="bg-gradient-to-br from-black via-gray-900 to-black text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl font-bold mb-6">Paint Protection Film in Poway, CA</h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-4">
              Protect your vehicle investment with professional paint protection film installation serving Poway, California. Our installers deliver comprehensive PPF coverage for trucks, SUVs, Tesla, and family vehicles—preventing rock chips, scratches, and environmental damage from Poway's highway access roads and daily commuting routes.
            </p>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              Serving Poway, Rancho Bernardo, Scripps Ranch, and inland San Diego County with bulk or precision-pattern installation, self-healing technology, and manufacturer-backed warranties. Located in nearby Vista, we're just 15 minutes from Poway Road via I-15, offering convenient appointments and expert installation for vehicles driven on I-15, Pomerado Road, and throughout North County's busiest corridors.
            </p>
            <div className="flex gap-4 justify-center flex-wrap">
              <a href="tel:858-282-8119" className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-3 rounded-lg font-semibold transition">
                <Phone size={18} />
                858-282-8119
              </a>
              <button onClick={() => { navigate("/contact"); setTimeout(() => window.scrollTo(0, 0), 100); }} className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold transition">
                <Calendar size={18} />
                Get Free PPF Quote
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Luxury Vehicle Protection */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Luxury Vehicle Paint Protection in Poway</h2>
          <div className="max-w-4xl mx-auto space-y-6 text-gray-300 text-lg">
            <p>
              Poway's family-oriented community includes luxury vehicle owners who demand professional paint protection for high-end automobiles. We specialize in PPF installation for Porsche Cayenne, Macan, and 911 models popular among Poway professionals and executives commuting to Rancho Bernardo, Scripps Ranch, and Sorrento Valley employment centers. BMW X5, X7, and M-series vehicles receive precision coverage protecting German engineering from I-15 highway debris and Poway's intense UV exposure.
            </p>
            <p>
              Mercedes-Benz luxury SUVs, AMG performance vehicles, and premium sedans driven throughout Stoneridge, Maderas, and Poway's upscale neighborhoods benefit from our professional installation expertise. <a href="/tesla-ppf-san-diego" className="text-cyan-400 hover:underline">Tesla paint protection</a> serves Poway's growing electric vehicle market, with Model 3, Model Y, Model S, and Model X requiring specialized PPF coverage against highway rock chips and inland heat. Audi vehicles including Q7, Q8, A6, and RS models receive bulk or precision-pattern coverage selected for European paint and bodywork.
            </p>
            <p>
              Range Rover, Lexus, Acura, and premium family vehicles benefit from our professional <a href="/paint-protection-film" className="text-cyan-400 hover:underline">PPF installation services</a> protecting investments in North County San Diego's desirable inland community. Our Vista facility—just 15 minutes from Poway via I-15—provides convenient access, flexible scheduling, and expert installation worthy of luxury automobiles serving Poway families for years of reliable, protected driving.
            </p>
          </div>
        </div>
      </section>

      {/* Why Poway Needs PPF */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Why Poway Vehicle Owners Choose Paint Protection Film</h2>
          <div className="max-w-4xl mx-auto space-y-6 text-gray-300 text-lg">
            <p>
              Poway's strategic location at the intersection of I-15 and Pomerado Road creates high-risk driving conditions for automotive paint protection. Daily commuters traveling to Rancho Bernardo, Scripps Ranch, Mira Mesa, or downtown San Diego face constant rock chip bombardment, gravel spray, and highway debris. <a href="/paint-protection-film" className="text-cyan-400 hover:underline">Paint protection film</a> prevents the cumulative paint damage that occurs from years of Poway highway commuting, preserving factory finish and maintaining resale value.
            </p>
            <p>
              Poway residents drive practical family vehicles alongside luxury automobiles—Ford F-150 and Super Duty trucks, Chevrolet Silverado and Tahoe, RAM trucks, Toyota 4Runner and Tacoma, Tesla Model 3 and Model Y, BMW X5, Mercedes GLE, and Jeep Wrangler. These vehicles serve Poway families for work, recreation, and daily transportation, making paint protection a smart investment against the wear and tear of active lifestyles. PPF installation maintains appearance and prevents the paint chips, scratches, and damage that reduce vehicle value and require expensive repair.
            </p>
            <p>
              Poway's inland location means intense summer heat and UV exposure that accelerates paint fading and clear coat oxidation. Vehicles parked at Poway Center, Poway Commons, or Old Poway Park experience year-round sun damage that causes color fading and paint chalking. Premium PPF includes UV-resistant properties that prevent sun damage while maintaining optical clarity, keeping your vehicle's paint vibrant and protected for 10+ years despite Poway's harsh inland climate.
            </p>
            <p>
              Poway's active outdoor community means vehicles encounter unique protection challenges. Off-road adventures on nearby trails, mountain biking equipment transport, and family beach trips to Oceanside or Carlsbad subject vehicles to debris, sand abrasion, and contact damage. PPF protects against these varied environmental challenges, ensuring your truck or SUV maintains showroom condition regardless of how you use it in Poway's recreation-focused lifestyle.
            </p>
            <p>
              We serve Poway customers with the same premium installation quality provided to La Jolla, Del Mar, and Rancho Santa Fe luxury vehicle owners. Our Vista facility offers convenient scheduling just 15 minutes from Poway via I-15, with indoor installation bays, experienced technicians, and transparent pricing. Whether protecting a work truck, family SUV, or Tesla, we deliver professional results backed by manufacturer warranties and installation workmanship guarantees.
            </p>
          </div>
        </div>
      </section>

      {/* Tesla PPF Section */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Tesla Paint Protection Film for Poway Owners</h2>
          <div className="max-w-4xl mx-auto space-y-6 text-gray-300 text-lg mb-12">
            <p>
              Poway has growing Tesla ownership, particularly Model 3, Model Y, and Model X vehicles popular with families in Stoneridge, Maderas, and throughout the community. Tesla's water-based paint requires professional protection against I-15 highway rock chips and Poway's intense UV exposure. <a href="/tesla-ppf-san-diego" className="text-cyan-400 hover:underline">Tesla PPF installation</a> preserves factory finish and prevents the paint degradation that occurs from daily commuting and sun exposure in Poway's inland environment.
            </p>
            <p>
              We install model-specific PPF patterns designed for Model 3, Model Y, Model S, Model X, and Cybertruck. Bulk or precision-pattern methods keep coverage complete across Tesla's unique body lines, flush door handles, and charging port areas. Our installation protects Autopilot sensors, cameras, and radar while delivering seamless coverage that's invisible to the eye. Many Poway Tesla owners schedule installation immediately after delivery to protect their investment from day one.
            </p>
            <p>
              Popular packages for Poway Tesla owners include full front coverage (starting at $1,899) for comprehensive highway protection against I-15 debris, and full vehicle coverage (starting at $4,499) for maximum resale value preservation. We also offer partial front options for budget-conscious owners who primarily need rock chip protection for daily Poway-to-Rancho Bernardo commuting.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Zap className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Model-Specific Patterns</h3>
              <p className="text-gray-300">Precision templates for every Tesla model ensure flawless fitment without cutting on your vehicle's paint.</p>
            </div>
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Shield className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Autopilot Safe</h3>
              <p className="text-gray-300">Proper installation around Tesla sensors and cameras ensures full Autopilot functionality with complete paint protection.</p>
            </div>
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Award className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Warranty Safe</h3>
              <p className="text-gray-300">PPF installation doesn't void Tesla factory warranty and helps maintain warranty-compliant paint condition.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Coverage Options */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">PPF Coverage Packages for Poway Vehicles</h2>
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Partial Front</h3>
              <p className="text-gray-300 mb-6">
                Essential coverage for high-impact zones: front bumper, partial hood, mirrors, headlights. Perfect for highway commuters.
              </p>
              <div className="text-cyan-400 text-3xl font-bold mb-2">Starting at $1,299</div>
              <p className="text-gray-400 text-sm mb-6">Pricing varies by vehicle</p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Front bumper full coverage</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Partial hood (12-18 inches)</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Mirrors and headlights</span>
                </li>
              </ul>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border-2 border-cyan-500">
              <div className="bg-cyan-500 text-black text-xs font-bold px-3 py-1 rounded-full inline-block mb-4">MOST POPULAR</div>
              <h3 className="text-2xl font-bold mb-4 text-white">Full Front</h3>
              <p className="text-gray-300 mb-6">
                Complete front-end protection: full hood, bumper, fenders, mirrors, headlights. Maximum I-15 highway protection.
              </p>
              <div className="text-cyan-400 text-3xl font-bold mb-2">Starting at $1,899</div>
              <p className="text-gray-400 text-sm mb-6">Pricing varies by vehicle</p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Full hood, fenders, and bumper</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Headlights, fog lights, mirrors</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Manufacturer-backed film warranty</span>
                </li>
              </ul>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Full Vehicle</h3>
              <p className="text-gray-300 mb-6">
                Ultimate protection covering every painted surface. Maintain showroom condition and maximize resale value.
              </p>
              <div className="text-cyan-400 text-3xl font-bold mb-2">Starting at $4,499</div>
              <p className="text-gray-400 text-sm mb-6">Pricing varies by vehicle</p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>All full front coverage areas</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Doors, rocker panels, quarters</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Roof, trunk, rear bumper</span>
                </li>
              </ul>
            </div>
          </div>
          <p className="text-center text-gray-400 text-sm">
            Starting prices shown are for standard vehicles. Exact pricing determined after free consultation and vehicle inspection.
          </p>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Why Poway Drivers Trust Our PPF Installation</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Shield className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Highway Protection</h3>
              <p className="text-gray-300">Defense against I-15 rock chips, gravel spray, and debris from Poway's heavy commuter traffic corridors.</p>
            </div>
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Sparkles className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Self-Healing Technology</h3>
              <p className="text-gray-300">Film repairs minor scratches from parking lots and daily wear with heat activation in Poway's sunny climate.</p>
            </div>
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Car className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Truck & SUV Expertise</h3>
              <p className="text-gray-300">Specialized installation for trucks, SUVs, and off-road vehicles popular in Poway's family-oriented community.</p>
            </div>
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Award className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Resale Value</h3>
              <p className="text-gray-300">Maintain pristine paint condition for maximum resale value when upgrading your family vehicle.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Installation Process */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Our Professional PPF Installation Process</h2>
          <div className="max-w-4xl mx-auto space-y-8">
            <div className="flex gap-6">
              <div className="bg-cyan-500 text-black font-bold w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl">1</div>
              <div>
                <h3 className="text-2xl font-bold mb-3 text-white">Free Consultation & Vehicle Assessment</h3>
                <p className="text-gray-300">We inspect your vehicle's paint condition, discuss protection goals, and provide transparent pricing. For Poway customers, we review commute routes and recommend coverage based on highway exposure, parking environment, and budget constraints.</p>
              </div>
            </div>
            <div className="flex gap-6">
              <div className="bg-cyan-500 text-black font-bold w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl">2</div>
              <div>
                <h3 className="text-2xl font-bold mb-3 text-white">Paint Preparation & Correction</h3>
                <p className="text-gray-300">Comprehensive wash, decontamination, and clay bar treatment remove embedded debris and surface contaminants. For vehicles with existing damage, we perform paint correction to eliminate swirl marks, water spots, and light scratches, creating a perfect surface for PPF adhesion.</p>
              </div>
            </div>
            <div className="flex gap-6">
              <div className="bg-cyan-500 text-black font-bold w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl">3</div>
              <div>
                <h3 className="text-2xl font-bold mb-3 text-white">Precision Installation with Vehicle-Specific Templates</h3>
                <p className="text-gray-300">Using bulk film or precision-cut patterns selected for your exact vehicle make and model, we apply premium self-healing PPF with professional wet installation techniques. Proper edge wrapping, heat forming, and squeegee methods ensure seamless coverage across complex curves without cutting on the vehicle.</p>
              </div>
            </div>
            <div className="flex gap-6">
              <div className="bg-cyan-500 text-black font-bold w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl">4</div>
              <div>
                <h3 className="text-2xl font-bold mb-3 text-white">Quality Inspection & Delivery</h3>
                <p className="text-gray-300">Final inspection ensures perfect edges, no bubbles, complete adhesion, and proper curing. We provide care instructions, warranty documentation, and maintenance recommendations before returning your vehicle to Poway with invisible protection and factory-fresh appearance.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Local Context */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Paint Protection for Poway's Driving Environment</h2>
          <div className="max-w-4xl mx-auto space-y-6 text-gray-300 text-lg">
            <p>
              Poway's location along I-15 creates constant rock chip exposure for vehicles commuting to employment centers in Rancho Bernardo, Scripps Ranch, Sorrento Valley, and downtown San Diego. I-15 traffic includes heavy construction vehicles, gravel trucks, and commercial traffic that spray debris across all lanes. A single morning commute can result in multiple rock chips to unprotected front-facing surfaces. Professional PPF installation eliminates this cumulative damage, preserving factory paint despite daily highway exposure.
            </p>
            <p>
              Pomerado Road, Twin Peaks Road, and Espola Road serve as Poway's main arterial routes, connecting residential neighborhoods to schools, shopping, and recreation areas. These busy roads experience constant traffic and debris, creating additional paint damage risks beyond highway commuting. PPF protects against the varied impact damage that occurs during typical Poway daily driving, from school drop-off at Poway Unified campuses to shopping at Poway Center and Old Poway Park visits.
            </p>
            <p>
              Poway's inland climate means summer temperatures exceeding 90°F and intense UV exposure that accelerates paint oxidation and clear coat failure. Vehicles parked outdoors at Poway High School, Lake Poway, or neighborhood driveways experience year-round sun damage. Premium PPF includes UV-resistant properties that prevent fading while maintaining optical clarity. The hydrophobic top coat also repels water, making vehicle maintenance easier in Poway's dry, dusty environment.
            </p>
            <p>
              Poway families use vehicles for diverse purposes—work commuting, recreational trail access, beach trips, and youth sports transportation. Truck and SUV owners transport mountain bikes, camping gear, kayaks, and equipment that can cause bed liner scratches, tailgate damage, and loading area wear. PPF protects these high-contact zones, maintaining resale value and appearance despite Poway's active outdoor lifestyle demands.
            </p>
            <p>
              Our Vista facility serves Poway customers with convenient 15-minute access via I-15 southbound. We understand Poway vehicle owners need practical, durable protection that withstands real-world driving conditions—not just cosmetic enhancement. Professional PPF installation delivers long-term value by preventing paint damage, maintaining factory appearance, and protecting your investment in vehicles that serve Poway families daily for years.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Poway PPF FAQs</h2>
          <div className="space-y-6">
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">How much does PPF cost in Poway?</h3>
              <p className="text-gray-300">Partial front PPF starts at $1,299, full front starts at $1,899, and full vehicle coverage starts at $4,499. Pricing varies by vehicle make and model. We provide detailed, transparent quotes for Poway residents with no hidden fees or surprise charges.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Is PPF worth it for Poway's driving conditions?</h3>
              <p className="text-gray-300">Yes. Poway's highway access via I-15 and Pomerado Road means constant rock chip exposure from daily commuting. PPF prevents cumulative damage that would otherwise require expensive paint repair or reduce resale value. The investment pays for itself by avoiding future body shop costs.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">How long does PPF last in Poway?</h3>
              <p className="text-gray-300">Premium PPF lasts 10+ years in Poway's inland climate. Self-healing technology and UV resistance ensure long-term protection against sun damage, heat, and environmental wear. The film maintains optical clarity and adhesion throughout its lifespan with minimal maintenance.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Can you install PPF on trucks and SUVs in Poway?</h3>
              <p className="text-gray-300">Absolutely. We install PPF on Ford F-150, Chevrolet Silverado, RAM trucks, Toyota 4Runner, Jeep Wrangler, and all SUV models popular in Poway. Coverage includes front bumpers, hoods, fenders, rocker panels, bed rails, and tailgates for comprehensive truck protection.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">How long does installation take?</h3>
              <p className="text-gray-300">Partial front coverage takes 1 day. Full front requires 2 days. Full vehicle PPF installation takes 3-5 days depending on vehicle size and complexity. Larger trucks and SUVs may require additional time for proper coverage of all panels and surfaces.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Does PPF protect against Poway's summer heat?</h3>
              <p className="text-gray-300">Yes. PPF includes UV-resistant properties that prevent sun damage, clear coat oxidation, and paint fading from Poway's intense summer temperatures. The film maintains integrity and appearance even when exposed to 90°F+ heat and direct sunlight year-round.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Can PPF be installed on used vehicles?</h3>
              <p className="text-gray-300">Yes. We perform paint correction to remove existing swirl marks, water spots, and light scratches before PPF installation. This restores a flawless finish that PPF then protects long-term. Even older vehicles benefit from PPF preventing future damage.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Do you offer mobile installation in Poway?</h3>
              <p className="text-gray-300">PPF requires controlled indoor conditions with proper lighting and a clean workspace. Mobile installation cannot achieve professional quality standards. We invite Poway residents to our Vista facility, just 15 minutes away via I-15, for expert installation in indoor conditions.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-16 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-8 text-white text-center">Related Services</h2>
          <p className="text-gray-300 text-center mb-8">
            Complete vehicle protection services for Poway drivers:
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            <a href="/paint-protection-film-san-diego" className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">Paint Protection Film San Diego</h3>
              <p className="text-gray-400 text-sm">Full PPF pricing, coverage options, and installation guide for San Diego</p>
            </a>
            <a href="/tesla-ppf-san-diego" className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">Tesla PPF San Diego</h3>
              <p className="text-gray-400 text-sm">Specialized PPF installation for all Tesla models with precision patterns</p>
            </a>
            <a href="/ppf-san-diego" className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">PPF San Diego</h3>
              <p className="text-gray-400 text-sm">San Diego paint protection film services for all vehicles</p>
            </a>
            <a href="/window-tint-san-diego" className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">Window Tint San Diego</h3>
              <p className="text-gray-400 text-sm">Ceramic tint for heat rejection and UV protection in Poway's inland climate</p>
            </a>
          </div>
        </div>
      </section>

      {/* Service Area */}
      <section className="py-16 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-8 text-white text-center flex items-center justify-center gap-3">
            <MapPin className="text-cyan-400" size={32} />
            Serving Poway & Inland San Diego County
          </h2>
          <p className="text-gray-300 text-lg mb-6 text-center">
            Our Vista facility is conveniently located 15 minutes from Poway via I-15, serving vehicle owners throughout inland San Diego with professional PPF installation.
          </p>
          <div className="grid md:grid-cols-2 gap-4 text-gray-300">
            <div className="bg-black p-4 rounded-lg border border-cyan-500/20">
              <h3 className="font-bold text-white mb-2">Poway & Surrounding Areas</h3>
              <ul className="space-y-1 text-sm">
                <li>• Poway (all neighborhoods)</li>
                <li>• Rancho Bernardo</li>
                <li>• Scripps Ranch</li>
                <li>• Sabre Springs</li>
                <li>• Carmel Mountain Ranch</li>
                <li>• 4S Ranch</li>
              </ul>
            </div>
            <div className="bg-black p-4 rounded-lg border border-cyan-500/20">
              <h3 className="font-bold text-white mb-2">North County Cities</h3>
              <ul className="space-y-1 text-sm">
                <li>• <a href="/ppf-san-marcos" className="text-cyan-400 hover:underline">San Marcos</a></li>
                <li>• <a href="/ppf-escondido" className="text-cyan-400 hover:underline">Escondido</a></li>
                <li>• <a href="/ppf-vista" className="text-cyan-400 hover:underline">Vista</a></li>
                <li>• <a href="/ppf-oceanside" className="text-cyan-400 hover:underline">Oceanside</a></li>
                <li>• Ramona</li>
              </ul>
            </div>
          </div>
          <p className="text-gray-300 text-center mt-8">
            <strong className="text-white">Shop Address:</strong> 2244 South Santa Fe Avenue, Suite A7, Vista, CA 92084
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-cyan-500 to-cyan-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6 text-white">Protect Your Poway Vehicle with Professional PPF</h2>
          <p className="text-xl text-white/90 mb-8">
            Schedule your free consultation and get a custom quote for paint protection film installation. Convenient appointments for Poway residents.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => { navigate("/contact"); setTimeout(() => window.scrollTo(0, 0), 100); }} className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2">
              <Calendar size={20} />
              Get Free PPF Quote
            </button>
            <a href="tel:858-282-8119" className="bg-white hover:bg-gray-100 text-gray-900 font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2">
              <Phone size={20} />
              Call: 858-282-8119
            </a>
          </div>
        </div>
      </section>
      </div>
    </>
  )
}

export default PpfPowayPage
